import {
  G1N5,
  G1N6,
  KVAnnotation,
  KVBoundingBox,
  KVBoundingBoxJSON,
  KVCameraView,
  KVCameraViewJSON,
  KVCoordinate,
  KVDataLayersConfig,
  KVDataLayersConfigJSON,
  KVMeasurement,
  KVMeasurementJSON,
  KVPlaneIntersection,
  KVPlaneIntersectionJSON,
  KVPrimitive,
  KVReferencePlane,
  KVReferencePlaneJSON,
  KVSceneSettings,
  KVUnitType,
  normalizeKVUnit,
  RolodexEntryJson,
  WorldPosition,
} from 'types'
import { GeckoEntryBase } from 'types/entries/geckoEntry'
import {
  AutomatedDefectDetectionModelType,
  isAutomatedDefectDetectionModelType,
} from 'types/kestrelViewer/automatedDefectDetectionModel'
import { KestrelInspectionType } from 'types/kestrelViewer/inspectionType'
import { KVInspectionArea } from 'types/kestrelViewer/KVInspectionArea'
import {
  parseBoolean,
  parseFiniteNumber,
  parseString,
} from 'utils/parsingHelpers'

export interface KVAerospaceMetadata {
  missionDesignSeries?: string
  serialNumber?: string
}

export type KVModelFileRole = 'scan' | 'reference'
export type KVMeasurementScaleUnit = 'mm' | 'cm' | 'm' | 'custom'
export type KVNaniteFallbackTarget =
  | 'auto'
  | 'percent-triangles'
  | 'relative-error'

export interface KVCollisionSettings {
  fallbackTarget: KVNaniteFallbackTarget
  fallbackPercentTriangles?: number
  fallbackRelativeError?: number
}

export const DEFAULT_KV_COLLISION_SETTINGS: KVCollisionSettings = {
  fallbackTarget: 'auto',
}

export const KV_MEASUREMENT_SCALE_BY_UNIT: Record<
  Exclude<KVMeasurementScaleUnit, 'custom'>,
  number
> = {
  mm: 0.1,
  cm: 1,
  m: 100,
}

export const DEFAULT_KV_MEASUREMENT_SCALE_UNIT: KVMeasurementScaleUnit = 'mm'
export const DEFAULT_KV_MEASUREMENT_SCALE =
  KV_MEASUREMENT_SCALE_BY_UNIT[DEFAULT_KV_MEASUREMENT_SCALE_UNIT]

export interface KVModelFile {
  filename: string
  role: KVModelFileRole
  stage?: string | undefined
  inspectionDate?: string | undefined
}

export interface KVModelMetadata {
  fileSizeBytes?: number
  pakSizeBytes?: number
}

export const KV_DLC_BUILD_STATUSES = [
  'pending',
  'running',
  'succeeded',
  'failed',
  'cancelled',
] as const

export type KVDlcBuildStatus = (typeof KV_DLC_BUILD_STATUSES)[number]

export interface KVDlcBuild {
  status: KVDlcBuildStatus
  buildId?: string
  createTime?: string
  startTime?: string
  finishTime?: string
  failureDetail?: string
}

const parseAutomatedDefectDetectionModelType = (
  value: unknown
): AutomatedDefectDetectionModelType | undefined => {
  const parsed = parseString(value)
  return isAutomatedDefectDetectionModelType(parsed) ? parsed : undefined
}

const parseDlcBuild = (input: unknown): KVDlcBuild | undefined => {
  if (!input || typeof input !== 'object') return undefined

  const record = input as Record<string, unknown>
  const status = parseString(record.status)
  // An unrecognized status means the writer is ahead of this client; showing nothing is
  // better than showing a misleading indicator.
  if (
    !status ||
    !(KV_DLC_BUILD_STATUSES as ReadonlyArray<string>).includes(status)
  ) {
    return undefined
  }

  return {
    status: status as KVDlcBuildStatus,
    buildId: parseString(record.buildId),
    createTime: parseString(record.createTime),
    startTime: parseString(record.startTime),
    finishTime: parseString(record.finishTime),
    failureDetail: parseString(record.failureDetail),
  }
}

export interface KVLiteBuild {
  manifestVersion: number
  generatedAt?: string
  totalTriangles?: number
}

const parseLiteBuild = (input: unknown): KVLiteBuild | undefined => {
  if (!input || typeof input !== 'object') return undefined

  const record = input as Record<string, unknown>
  const manifestVersion = parseFiniteNumber(record.manifestVersion)
  if (manifestVersion === undefined) return undefined

  return {
    manifestVersion,
    generatedAt: parseString(record.generatedAt),
    totalTriangles: parseFiniteNumber(record.totalTriangles),
  }
}

export interface KVImportSettings {
  naniteEnabled: boolean
  twoSidedModel: boolean
  twoSidedCollision: boolean
  collisionSettings: KVCollisionSettings
  runAutomatedDefectDetection: boolean
  /** Vertex batch contract. custom or automl. */
  automatedDefectDetectionModelType?: AutomatedDefectDetectionModelType
  /**
   * Vertex model resource name last run on this entry, so the picker starts
   * where the user left off. The server still authorizes it on every run.
   */
  automatedDefectVertexModelId?: string
  /** Minimum detection confidence for Vertex batch JSONL and deconflict (0–1). */
  automatedDefectConfidenceThreshold: number
  /** 3D clustering radius in meters for deconfliction (> 0). */
  automatedDefectClusteringDistanceM: number
}

export class GeckoKestrelModel extends GeckoEntryBase<
  {
    displayName?: string
    inspectionAreas: KVInspectionArea[]
    annotations: KVAnnotation[]
    measurements: KVMeasurement[]
    cameraViews: KVCameraView[]
    planeIntersections: KVPlaneIntersection[]
    referencePlanes: KVReferencePlane[]
    primitives: KVPrimitive[]
    measurementScale: number
    measurementScaleUnit: KVMeasurementScaleUnit
    photosEnabled: boolean
    g1n5: G1N5 | undefined
    g1n6: G1N6 | undefined
    inspection_type?: KestrelInspectionType
    sceneSettings: KVSceneSettings
    dataLayersConfig: KVDataLayersConfig
    underlyingAsset: string
    scanDate: number
    origin?: KVCoordinate
    boundingBox?: KVBoundingBox
    defaultUnit?: KVUnitType
    cameraPosesScale: number
    boundingSegments?: WorldPosition[]
    dlcEnabled?: boolean
    kestrelLiteEnabled?: boolean
    dlcBuild?: KVDlcBuild
    liteBuild?: KVLiteBuild
    forms?: Record<string, string>
    photoRotations?: Record<string, number>
    assessment?: string
    modelFiles?: KVModelFile[]
    aerospaceMetadata?: KVAerospaceMetadata
    importSettings: KVImportSettings
    rawImportSettings: Record<string, unknown>
    modelTransform?: {
      location: [number, number, number]
      rotation: [number, number, number]
      scale: [number, number, number]
    }
    totalVertices?: number
    totalFaces?: number
    modelMetadata: KVModelMetadata
    archived: boolean
  },
  {
    organization: string
    site: string
    unit: string
    component: string
    camerasEntry: string
  }
> {
  private constructor(json: RolodexEntryJson) {
    const recordData = json.data as Record<string, unknown>

    const annotations = new Array<KVAnnotation>()
    const measurements = new Array<KVMeasurement>()
    const cameraViews = new Array<KVCameraView>()
    const planeIntersections = new Array<KVPlaneIntersection>()
    const inspectionAreas = new Array<KVInspectionArea>()
    const primitives = new Array<KVPrimitive>()
    const referencePlanes: KVReferencePlane[] = []
    const storedMeasurementScale = parseFiniteNumber(
      recordData?.measurementScale
    )
    const hasStoredMeasurementScale =
      storedMeasurementScale !== undefined && storedMeasurementScale > 0
    const measurementScale = hasStoredMeasurementScale
      ? storedMeasurementScale
      : DEFAULT_KV_MEASUREMENT_SCALE
    const storedMeasurementScaleUnit = parseString(
      recordData?.measurementScaleUnit
    )
    const measurementScaleUnit: KVMeasurementScaleUnit =
      storedMeasurementScaleUnit === 'mm' ||
      storedMeasurementScaleUnit === 'cm' ||
      storedMeasurementScaleUnit === 'm' ||
      storedMeasurementScaleUnit === 'custom'
        ? storedMeasurementScaleUnit
        : hasStoredMeasurementScale
          ? 'custom'
          : DEFAULT_KV_MEASUREMENT_SCALE_UNIT
    const origin = recordData?.origin as KVCoordinate | undefined
    const scanDate = recordData?.scanDate as number | 0
    const underlyingAsset = recordData?.underlyingAsset as string | ''
    const photosEnabled = parseBoolean(recordData?.photosEnabled) ?? false
    const displayName = parseString(recordData?.displayName)
    const dlcEnabled = parseBoolean(recordData?.dlcEnabled) ?? true
    const kestrelLiteEnabled =
      parseBoolean(recordData?.kestrelLiteEnabled) ?? false
    const dlcBuild = parseDlcBuild(recordData?.dlcBuild)
    const liteBuild = parseLiteBuild(recordData?.liteBuild)
    const archived = parseBoolean(recordData?.archived) ?? false
    const inspectionType =
      (recordData?.inspection_type as KestrelInspectionType | undefined) ??
      KestrelInspectionType.DEFAULT

    const boundingBox = recordData?.boundingBox
      ? KVBoundingBox.fromJSON(recordData.boundingBox as KVBoundingBoxJSON)
      : undefined

    const boundingSegments = recordData?.boundingSegments as WorldPosition[]

    const defaultUnit = normalizeKVUnit(
      (recordData as Record<string, unknown>)?.defaultUnit
    )

    let g1n5: G1N5 = new G1N5()
    let g1n6: G1N6 = new G1N6()
    let sceneSettings: KVSceneSettings = new KVSceneSettings()
    let dataLayersConfig: KVDataLayersConfig = new KVDataLayersConfig()

    if (recordData.annotations) {
      const annotationJSONs = recordData.annotations as Record<
        string,
        unknown
      >[]
      for (const annotation of annotationJSONs) {
        annotations.push(KVAnnotation.fromJSON(annotation))
      }
    }

    if (recordData.measurements) {
      const measurementJSONs = recordData.measurements as KVMeasurementJSON[]
      for (const measurement of measurementJSONs) {
        measurements.push(KVMeasurement.fromJSON(measurement))
      }
    }

    if (recordData.planeIntersections) {
      const planeIntersectionJSONs =
        recordData.planeIntersections as KVPlaneIntersectionJSON[]
      for (const planeIntersection of planeIntersectionJSONs) {
        planeIntersections.push(KVPlaneIntersection.fromJSON(planeIntersection))
      }
    }

    if (recordData.inspectionAreas) {
      const inspectionAreaJSONs = recordData.inspectionAreas as Record<
        string,
        unknown
      >[]
      for (const inspectionArea of inspectionAreaJSONs) {
        inspectionAreas.push(KVInspectionArea.fromJSON(inspectionArea))
      }
    }

    if (recordData.primitives) {
      const primitiveJSONs = recordData.primitives as Record<string, unknown>[]
      for (const primitive of primitiveJSONs) {
        primitives.push(KVPrimitive.fromJSON(primitive))
      }
    }

    if (recordData.referencePlanes) {
      const planeJSONs = recordData.referencePlanes as KVReferencePlaneJSON[]
      for (const planeJSON of planeJSONs) {
        referencePlanes.push(KVReferencePlane.fromJSON(planeJSON))
      }
    } else if (recordData.referencePlane) {
      referencePlanes.push(
        KVReferencePlane.fromJSON(
          recordData.referencePlane as KVReferencePlaneJSON
        )
      )
    }

    if (recordData.g1n5) {
      g1n5 = G1N5.fromJSON(recordData.g1n5 as Record<string, unknown>)
    }

    if (recordData.g1n6) {
      g1n6 = G1N6.fromJSON(recordData.g1n6)
    }

    if (recordData.cameraViews) {
      const cameraViewJSONs = recordData.cameraViews as KVCameraViewJSON[]
      for (const cameraView of cameraViewJSONs) {
        cameraViews.push(KVCameraView.fromJSON(cameraView))
      }
    }

    if (recordData.sceneSettings) {
      sceneSettings = KVSceneSettings.fromJSON(recordData.sceneSettings)
    }

    if (recordData.dataLayersConfig) {
      dataLayersConfig = KVDataLayersConfig.fromJSON(
        recordData.dataLayersConfig as KVDataLayersConfigJSON
      )
    }

    const cameraPosesScale =
      (recordData?.cameraPosesScale as number | undefined) ?? 0.01

    const forms = recordData?.forms as Record<string, string> | undefined

    const photoRotations = recordData?.photoRotations as
      | Record<string, number>
      | undefined

    const assessment = parseString(recordData?.assessment)
    const modelFiles = Array.isArray(recordData?.modelFiles)
      ? recordData.modelFiles
          .map((item): KVModelFile | undefined => {
            if (!item || typeof item !== 'object') return undefined

            const modelFileRecord = item as Record<string, unknown>
            const filename = parseString(modelFileRecord.filename)
            const role = parseString(modelFileRecord.role)
            const stage = parseString(modelFileRecord.stage)
            const inspectionDate = parseString(modelFileRecord.inspectionDate)

            if (!filename || (role !== 'scan' && role !== 'reference')) {
              return undefined
            }

            return { filename, role, stage, inspectionDate }
          })
          .filter((item): item is KVModelFile => Boolean(item))
      : undefined

    const aerospaceMetadata = recordData?.aerospaceMetadata as
      | KVAerospaceMetadata
      | undefined
    const totalVertices = recordData?.totalVertices as number | undefined
    const totalFaces = recordData?.totalFaces as number | undefined
    const modelMetadataRecord = recordData?.modelMetadata as
      | Record<string, unknown>
      | undefined
    const modelMetadata: KVModelMetadata = {
      fileSizeBytes: parseFiniteNumber(modelMetadataRecord?.fileSizeBytes),
      pakSizeBytes: parseFiniteNumber(modelMetadataRecord?.pakSizeBytes),
    }

    const importSettingsRecord = recordData?.importSettings as
      | Record<string, unknown>
      | undefined
    const twoSidedModel =
      parseBoolean(importSettingsRecord?.twoSidedModel) ?? true
    const collisionSettingsRecord = importSettingsRecord?.collisionSettings as
      | Record<string, unknown>
      | undefined
    const rawFallbackTarget = parseString(
      collisionSettingsRecord?.fallbackTarget
    )
    const fallbackTarget: KVNaniteFallbackTarget = [
      'percent-triangles',
      'relative-error',
    ].includes(rawFallbackTarget ?? '')
      ? (rawFallbackTarget as KVNaniteFallbackTarget)
      : 'auto'
    const collisionSettings: KVCollisionSettings = {
      fallbackTarget,
      fallbackPercentTriangles: parseFiniteNumber(
        collisionSettingsRecord?.fallbackPercentTriangles
      ),
      fallbackRelativeError: parseFiniteNumber(
        collisionSettingsRecord?.fallbackRelativeError
      ),
    }
    const importSettings: KVImportSettings = {
      naniteEnabled: parseBoolean(importSettingsRecord?.naniteEnabled) ?? true,
      twoSidedModel,
      twoSidedCollision:
        parseBoolean(importSettingsRecord?.twoSidedCollision) ?? twoSidedModel,
      collisionSettings,
      runAutomatedDefectDetection:
        parseBoolean(importSettingsRecord?.runAutomatedDefectDetection) ??
        false,
      automatedDefectDetectionModelType: parseAutomatedDefectDetectionModelType(
        importSettingsRecord?.automatedDefectDetectionModelType
      ),
      automatedDefectVertexModelId: parseString(
        importSettingsRecord?.automatedDefectVertexModelId
      ),
      automatedDefectConfidenceThreshold:
        parseFiniteNumber(
          importSettingsRecord?.automatedDefectConfidenceThreshold
        ) ?? 0.5,
      automatedDefectClusteringDistanceM:
        parseFiniteNumber(
          importSettingsRecord?.automatedDefectClusteringDistanceM
        ) ?? 0.5,
    }

    const modelTransform = recordData?.modelTransform as
      | {
          location: [number, number, number]
          rotation: [number, number, number]
          scale: [number, number, number]
        }
      | undefined

    super(
      json,
      {
        inspectionAreas: inspectionAreas,
        annotations: annotations,
        displayName: displayName,
        measurements: measurements,
        cameraViews: cameraViews,
        planeIntersections: planeIntersections,
        referencePlanes: referencePlanes,
        primitives: primitives,
        measurementScale: measurementScale,
        measurementScaleUnit: measurementScaleUnit,
        photosEnabled: photosEnabled,
        g1n5: g1n5,
        g1n6: g1n6,
        inspection_type: inspectionType,
        sceneSettings: sceneSettings,
        dataLayersConfig: dataLayersConfig,
        origin: origin,
        scanDate: scanDate,
        underlyingAsset: underlyingAsset,
        boundingBox: boundingBox,
        defaultUnit: defaultUnit,
        cameraPosesScale: cameraPosesScale,
        boundingSegments: boundingSegments,
        dlcEnabled: dlcEnabled,
        kestrelLiteEnabled: kestrelLiteEnabled,
        dlcBuild: dlcBuild,
        liteBuild: liteBuild,
        forms: forms,
        photoRotations: photoRotations,
        assessment: assessment,
        modelFiles: modelFiles,
        aerospaceMetadata: aerospaceMetadata,
        importSettings: importSettings,
        rawImportSettings: { ...importSettingsRecord },
        modelTransform: modelTransform,
        totalVertices: totalVertices,
        totalFaces: totalFaces,
        modelMetadata: modelMetadata,
        archived: archived,
      },
      {
        organization: parseString(json.tags.organization) ?? '',
        site: parseString(json.tags.site) ?? '',
        unit: parseString(json.tags.unit) ?? '',
        component: parseString(json.tags.component) ?? '',
        camerasEntry: parseString(json.tags['kestrel-model-cameras']) ?? '',
      },
      {}
    )
  }

  public static CreateFromJSON(json: RolodexEntryJson) {
    return new GeckoKestrelModel(json)
  }
}
